import React from "react";

import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/storage";
import "firebase/compat/firestore";

import { getRecoil } from "recoil-nexus";
import Bugsnag from "@bugsnag/js";
import _ from "underscore";

import organizationIdSelector from "../atoms/organizationIdSelector";
import { nanoid } from "nanoid";
import { consola } from "consola";
import { useAdminDraftOptional } from "../context/AdminDraftContext";

export type ProgressPhotoData = {
  id?: string;
  name: string;
  index: number;
};

/**
 * Hook for managing progress photo TYPE CONFIGURATION (not actual client photos)
 * This manages the organization-wide settings for what photo positions are available
 * Examples: "Front", "Side", "Back", etc.
 * 
 * For managing actual client progress photos, use useClientPhotos instead.
 */
const useProgressPhotos = () => {
  const organizationId = getRecoil(organizationIdSelector);
  const adminDraft = useAdminDraftOptional();

  const [liveProgressPhotos, setLiveProgressPhotos] =
    React.useState<ProgressPhotoData[]>();

  const progressPhotos = React.useMemo(() => {
    if (!adminDraft) return liveProgressPhotos;
    const wd = adminDraft.workingDraft;
    if (wd?.progressPhotoConfiguration !== undefined) {
      return _.sortBy(wd.progressPhotoConfiguration, "index");
    }
    return liveProgressPhotos;
  }, [adminDraft, liveProgressPhotos]);

  const savePhotoTypeConfig = React.useCallback(
    async function (newProgressPhotos: any) {
      if (adminDraft) {
        adminDraft.updateProgressPhotoConfiguration(newProgressPhotos);
        return;
      }
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId);
      try {
        consola.debug("+++++ WRITE => useProgressPhotos: savePhotoTypeConfig")
        await ref.set(
          {
            progressPhotoConfiguration: newProgressPhotos,
          },
          { merge: true },
        );
      } catch (e) {
        Bugsnag.notify(new Error("Error saving Progress Photo data"));
      }
    },
    [organizationId, adminDraft],
  );

  const addPhotoType = React.useCallback(
    async function (name: string) {
      const progressPhotosCopy = _.clone(progressPhotos || []);
      progressPhotosCopy.push({
        id: nanoid(),
        name: name,
        index: progressPhotosCopy.length
      });

      await savePhotoTypeConfig(progressPhotosCopy);
    },
    [progressPhotos, savePhotoTypeConfig],
  );

  // TODO: Deleting a photo type should activate a cloud function to remove references.
  const deletePhotoType = React.useCallback(
    async function (id: string) {
      if (progressPhotos) {
        const progressPhotosCopy = _.clone(progressPhotos);
        const index = _.findIndex(progressPhotosCopy, { id: id });
        if (index !== -1) {
          progressPhotosCopy.splice(index, 1);

          // Reindex
          for (let i = 0; i < progressPhotosCopy.length; i++) {
            progressPhotosCopy[i].index = i + 1;
          }

          await savePhotoTypeConfig(progressPhotosCopy);
        }
      }
    },
    [organizationId, progressPhotos, savePhotoTypeConfig],
  );

  const getPhotoTypeById = React.useCallback(
    (id: string) => {
      if (!progressPhotos) {
        return null;
      }
      const value = _.findWhere(progressPhotos, { id: id });
      return value;
    },
    [progressPhotos],
  );

  const movePhotoType = React.useCallback(
    async (id: any, direction: any) => {
      if (!progressPhotos) {
        return;
      }

      if (direction === "up") {
        const index = _.findIndex(progressPhotos, { id });
        if (index !== -1) {
          if (index > 0) {
            // get prev index photo
            const prevProgressPhoto = progressPhotos[index - 1];
            const currProgressPhoto = progressPhotos[index];

            // Swap indexes
            const prevPhotoIndex = prevProgressPhoto.index;
            prevProgressPhoto.index = currProgressPhoto.index;
            currProgressPhoto.index = prevPhotoIndex;

            // Save them
            await savePhotoTypeConfig(progressPhotos);
          }
        }
      } else {
        const index = _.findIndex(progressPhotos, { id });
        if (index !== -1) {
          if (index < progressPhotos.length - 1) {
            // get prev index photo
            const nextProgressPhoto = progressPhotos[index + 1];
            const currProgressPhoto = progressPhotos[index];

            // Swap indexes
            const newPhotoIndex = nextProgressPhoto.index;
            nextProgressPhoto.index = currProgressPhoto.index;
            currProgressPhoto.index = newPhotoIndex;

            // Save them
            await savePhotoTypeConfig(progressPhotos);
          }
        }
      }
    },
    [progressPhotos, savePhotoTypeConfig],
  );

  React.useEffect(() => {
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId);

    const subscription = ref.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> useProgressPhotos")
      const data: any = snapshot.data();

      if (!data) {
        setLiveProgressPhotos([]);
      } else {
        const sorted = _.sortBy(data.progressPhotoConfiguration, "index");

        setLiveProgressPhotos(sorted);
      }
    });

    return () => {
      subscription();
    };
  }, [organizationId]);

  return {
    progressPhotos: progressPhotos, // Keep as progressPhotos for backward compatibility
    photoTypes: progressPhotos, // Add clearer alias
    savePhotoTypeConfig,
    deletePhotoType,
    movePhotoType,
    getPhotoTypeById,
    addPhotoType,
    // Deprecated names for backward compatibility
    saveProgressPhoto: savePhotoTypeConfig,
    deleteProgressPhoto: deletePhotoType,
    moveProgressPhoto: movePhotoType,
    getProgressPhotoById: getPhotoTypeById,
    addProgressPhoto: addPhotoType,
  };
};

export default useProgressPhotos;
